import { Check } from "lucide-react";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type InvoiceReminderOffset = components["schemas"]["InvoiceReminderOffset"];

// Timeline order: earliest reminder first, late-payment chasers last.
const OPTIONS: { value: InvoiceReminderOffset; label: string; sub: string }[] = [
  { value: "OneWeekBefore", label: "1 week before", sub: "due date − 7 days" },
  { value: "TwoDaysBefore", label: "2 days before", sub: "due date − 2 days" },
  { value: "OnDueDate", label: "On due date", sub: "fires on the due date itself" },
  { value: "TwoDaysAfter", label: "2 days after", sub: "due date + 2 days" },
  { value: "OneWeekAfter", label: "1 week after", sub: "due date + 7 days" },
];

interface ReminderOffsetMultiSelectProps {
  value: InvoiceReminderOffset[];
  onChange: (next: InvoiceReminderOffset[]) => void;
  disabled?: boolean;
  // Rings unsaved changes, as other dirty fields in the org settings form do.
  dirty?: boolean;
}

// Array order is irrelevant: the backend dedupes and resolves time-of-fire.
export function ReminderOffsetMultiSelect({
  value,
  onChange,
  disabled = false,
  dirty = false,
}: ReminderOffsetMultiSelectProps) {
  const selected = new Set(value);

  function toggle(offset: InvoiceReminderOffset) {
    if (selected.has(offset)) {
      onChange(value.filter((v) => v !== offset));
    } else {
      // Keep canonical timeline order regardless of click order.
      const next = [...value, offset];
      next.sort(
        (a, b) =>
          OPTIONS.findIndex((o) => o.value === a) -
          OPTIONS.findIndex((o) => o.value === b),
      );
      onChange(next);
    }
  }

  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 rounded-md border border-transparent p-0.5 transition-shadow",
        dirty && "ring-2 ring-primary/40",
      )}
    >
      {OPTIONS.map((opt) => {
        const isSelected = selected.has(opt.value);
        return (
          <Button
            key={opt.value}
            type="button"
            size="sm"
            variant={isSelected ? "default" : "outline"}
            disabled={disabled}
            onClick={() => toggle(opt.value)}
            aria-pressed={isSelected}
            title={opt.sub}
            className={cn(
              "h-8 rounded-full px-3 text-xs font-medium",
              isSelected && "shadow-sm",
            )}
          >
            {isSelected ? <Check className="size-3.5" /> : null}
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

export function reminderOffsetsEqual(
  a: readonly InvoiceReminderOffset[],
  b: readonly InvoiceReminderOffset[],
): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((v, i) => v === right[i]);
}
